/**
 * src/components/ui/Dialog.jsx — modal dialog, replacing components/ui.jsx's
 * old `Modal`.
 *
 * A real `<dialog>` opened with `showModal()`, not a `<div role="dialog">` —
 * audit.md flagged the old Modal for exactly this ("gets none of the native
 * top-layer/backdrop/focus-trap behaviour for free"). `showModal()` gives
 * correct focus trapping, Escape-to-close, and focus restored to the
 * trigger on close, all natively — no hand-written focus-trap code, which
 * is exactly the kind of thing that's easy to get subtly wrong by hand.
 *
 * Light dismiss (click the backdrop to close) uses `closedby="any"`
 * (modern-web-guidance's `light-dismiss-a-dialog` guide) with the guide's
 * own MANDATORY manual fallback for browsers that don't support it yet
 * (Safari, as of the guide's browser table — "limited availability", below
 * this project's bar for using a feature with no fallback) — feature-
 * detected via `'closedBy' in HTMLDialogElement.prototype`.
 *
 * Kept to the same `{ title, onClose, children }` API the old Modal used,
 * so its six call sites (Calendar/Clients/Flash/Projects/FlashGallery) only
 * needed an import swap, not a rewrite — real page redesigns are Phase 4-6.
 */
import { useEffect, useId, useRef } from 'react';

/**
 * @param {{ title: string, onClose: () => void, children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Dialog({ title, onClose, children }) {
  const ref = useRef(null);
  // Unique per instance — a static id would collide if two dialogs ever
  // exist in the DOM at once (e.g. the component gallery demoing one
  // alongside other content).
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    el.showModal();

    // The dialog's own `close` event fires for every dismissal path — Escape,
    // closedby="any" light-dismiss, the manual fallback below, or a future
    // `<form method="dialog">` submit — so this is the one place `onClose`
    // needs wiring, not one handler per dismissal method.
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);

    // MANDATORY fallback from modern-web-guidance's light-dismiss-a-dialog
    // guide: browsers without `closedby` support (Safari, at time of
    // writing) get manual backdrop-click detection instead. A click lands on
    // the <dialog> element itself only when it's outside the content box —
    // clicks inside land on a descendant — so checking `e.target === el` is
    // sufficient without also comparing coordinates.
    let handleBackdropClick;
    if (!('closedBy' in HTMLDialogElement.prototype)) {
      handleBackdropClick = (e) => {
        if (e.target === el) el.close();
      };
      el.addEventListener('click', handleBackdropClick);
    }

    return () => {
      el.removeEventListener('close', handleClose);
      if (handleBackdropClick) el.removeEventListener('click', handleBackdropClick);
      // Deliberately NOT calling el.close() here. It looks like the right
      // defensive move for a real unmount (e.g. the page navigating away
      // mid-dialog) — but dialog.close() dispatches its `close` event
      // asynchronously (a queued task, not synchronous), and React 19
      // StrictMode's dev-only double-invoke (mount -> cleanup -> mount
      // again, on the same DOM node) means that queued event doesn't fire
      // until AFTER the second mount has already attached a fresh
      // `handleClose` — so the harmless synthetic cleanup pass ended up
      // permanently closing the dialog for real, every time, in dev.
      // Removing this call fixes it and loses nothing: a truly unmounted
      // <dialog> is detached from the document, and a detached dialog's
      // modal/top-layer state is released by the browser on its own —
      // nothing here depended on the explicit close(). Found via an
      // interactive Playwright check during Phase 3's keyboard walkthrough,
      // not by axe (which only inspects static markup, never this class of
      // bug) — see docs/redesign/progress.md.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <dialog ref={ref} className="ui-dialog" closedby="any" aria-labelledby={titleId}>
      <div className="ui-dialog-header">
        <h2 id={titleId}>{title}</h2>
        <button type="button" className="btn btn-sm" onClick={() => ref.current?.close()}>
          Close
        </button>
      </div>
      {children}
    </dialog>
  );
}
